import { z } from "zod";
import { isoDateStringSchema } from "./common";
import { listQueryBaseSchema, type ListResult } from "./list-query";

/**
 * Moroccan TVA rates (CGI art. 99). 0 is not a "no tax" convenience value —
 * it is the rate carried by an exempt or export-of-services line, which is a
 * real, legally distinct case from 20%. The set is closed on purpose: an
 * invoice carrying an invented rate is a defective invoice.
 */
export const MOROCCAN_TAX_RATES = [0, 7, 10, 14, 20] as const;
export type MoroccanTaxRate = (typeof MOROCCAN_TAX_RATES)[number];
export const DEFAULT_TAX_RATE = 20 satisfies MoroccanTaxRate;

export const taxRateSchema = z
  .union([z.number(), z.string().trim().regex(/^\d{1,2}$/)])
  .transform((v) => (typeof v === "string" ? Number(v) : v))
  .pipe(z.number())
  .refine(
    (v) => (MOROCCAN_TAX_RATES as readonly number[]).includes(v),
    `TVA rate must be one of ${MOROCCAN_TAX_RATES.join(", ")}`,
  )
  .transform((v) => v as MoroccanTaxRate);

export const INVOICE_STATUSES = ["draft", "issued", "paid", "cancelled"] as const;
export type InvoiceStatus = (typeof INVOICE_STATUSES)[number];
export const invoiceStatusSchema = z.enum(INVOICE_STATUSES);

/**
 * A credit note (facture d'avoir) is the only legal way to undo an issued
 * invoice in Morocco — an issued number can never be deleted or reused, so a
 * correction is a second document that cancels the first. It carries its own
 * series, hence a `kind` on the row rather than a boolean flag.
 */
export const INVOICE_KINDS = ["invoice", "credit_note"] as const;
export type InvoiceKind = (typeof INVOICE_KINDS)[number];
export const invoiceKindSchema = z.enum(INVOICE_KINDS);

/**
 * Money in MAD. Mirrors `expenseAmountSchema`'s parsing rules (see
 * schemas/expenses.ts for why `z.coerce.number()` is refused here) but allows
 * 0 and negative-free values only: an invoice line of 0 is a legitimate free
 * item, a negative one is not — a refund is a credit note, not a minus sign.
 */
export const invoiceMoneySchema = z
  .union([
    z.number(),
    z.string().trim().regex(/^\d+(\.\d{1,2})?$/, "amount allows at most 2 decimal places"),
  ])
  .transform((v) => (typeof v === "string" ? Number(v) : v))
  .pipe(
    z
      .number()
      .nonnegative("amount cannot be negative")
      .max(100_000_000, "amount is unrealistically large")
      .refine((v) => Number(v.toFixed(2)) === v, "amount allows at most 2 decimal places"),
  );

/** `2026-08`. The month the invoice bills for, distinct from its issue date. */
export const periodMonthSchema = z
  .string()
  .trim()
  .regex(/^\d{4}-(0[1-9]|1[0-2])$/, "period must look like 2026-08");

/**
 * ICE — Identifiant Commun de l'Entreprise. Exactly 15 digits, and mandatory
 * on both sides of a Moroccan invoice. Stored digits-only; the UI groups them
 * for display. Optional at the partner level (a foreign client has none) but
 * checked for shape whenever a value is present.
 */
export const iceSchema = z
  .string()
  .trim()
  .regex(/^\d{15}$/, "ICE must be exactly 15 digits");

const optionalText = (max: number) =>
  z
    .string()
    .trim()
    .max(max)
    .optional()
    .transform((v) => (v ? v : null))
    .nullable();

/**
 * Makes a *constrained* optional field tolerate an untouched form input.
 *
 * `.optional()` only permits `undefined`, but an untouched text input hands
 * react-hook-form `""` — and `""` fails a 15-digit ICE regex or an email
 * check before `.optional()` ever gets a say. Without this, the partner form
 * could not be saved unless both ICE and email were filled, which is the
 * opposite of what those fields are meant to be, and it failed *silently*
 * because a blocked `handleSubmit` never fires a request.
 *
 * Only needed where the inner schema constrains the string's shape;
 * `optionalText` already collapses "" to null on its own.
 */
function optionalWhenBlank<T extends z.ZodTypeAny>(schema: T) {
  return z.preprocess(
    (value) => (typeof value === "string" && value.trim() === "" ? null : value),
    schema.nullable().optional(),
  );
}

// ---------------------------------------------------------------- partners

export const invoicePartnerInputSchema = z.object({
  name: z.string().trim().min(1, "name is required").max(191),
  legalName: optionalText(191),
  address: optionalText(500),
  city: optionalText(120),
  ice: optionalWhenBlank(iceSchema),
  ifNumber: optionalText(32),
  rc: optionalText(32),
  email: optionalWhenBlank(z.string().trim().email().max(191)),
  phone: optionalText(32),
  notes: optionalText(2000),
});
export type InvoicePartnerInput = z.infer<typeof invoicePartnerInputSchema>;

export const invoicePartnerUpdateSchema = invoicePartnerInputSchema.partial();
export type InvoicePartnerUpdateInput = z.infer<typeof invoicePartnerUpdateSchema>;

export interface InvoicePartnerDTO {
  id: string;
  name: string;
  legalName: string | null;
  address: string | null;
  city: string | null;
  ice: string | null;
  ifNumber: string | null;
  rc: string | null;
  email: string | null;
  phone: string | null;
  notes: string | null;
  createdAt: string | null;
  updatedAt: string | null;
  deletedAt: string | null;
}

export const invoicePartnersListQuerySchema = listQueryBaseSchema.extend({
  q: z.string().trim().optional(),
});
export type InvoicePartnersListQuery = z.infer<typeof invoicePartnersListQuerySchema>;
export type InvoicePartnersListResult = ListResult<InvoicePartnerDTO>;

// ------------------------------------------------------------------ lines

export const invoiceLineInputSchema = z.object({
  description: z.string().trim().min(1, "description is required").max(1000),
  /**
   * Quantity is a plain count, not money: 1 for a monthly commission line,
   * higher for per-unit billing. Capped at 4 decimals so a pro-rata like
   * 0.5 month stays expressible without inviting float noise.
   */
  quantity: z
    .union([z.number(), z.string().trim().regex(/^\d+(\.\d{1,4})?$/)])
    .transform((v) => (typeof v === "string" ? Number(v) : v))
    .pipe(z.number().positive("quantity must be greater than 0").max(1_000_000)),
  unitPriceHt: invoiceMoneySchema,
});
export type InvoiceLineInput = z.infer<typeof invoiceLineInputSchema>;

export interface InvoiceLineDTO {
  id: string;
  position: number;
  description: string;
  quantity: number;
  unitPriceHt: number;
  totalHt: number;
}

// --------------------------------------------------------------- invoices

export const invoiceCreateSchema = z.object({
  partnerId: z.string().uuid("pick a partner"),
  issueDate: isoDateStringSchema,
  // Blank-tolerant for the same reason as `ice`/`email` above: these three are
  // usually left empty on the form, and a bare `.optional()` would reject the
  // `""` an untouched date input produces — silently blocking every invoice
  // that does not happen to carry a due date and a purchase order.
  dueDate: optionalWhenBlank(isoDateStringSchema),
  periodMonth: optionalWhenBlank(periodMonthSchema),
  subject: optionalText(300),
  poNumber: optionalText(64),
  poDate: optionalWhenBlank(isoDateStringSchema),
  taxRate: taxRateSchema.default(DEFAULT_TAX_RATE),
  notes: optionalText(2000),
  lines: z.array(invoiceLineInputSchema).min(1, "an invoice needs at least one line"),
});
export type InvoiceCreateInput = z.infer<typeof invoiceCreateSchema>;

/**
 * Update is only ever accepted on a draft (the service enforces that, not
 * this schema). `lines` is all-or-nothing: sending it replaces the whole set,
 * because a partial line patch would need stable client-side ids for rows the
 * form lets you freely add, delete and reorder.
 */
export const invoiceUpdateSchema = invoiceCreateSchema.partial();
export type InvoiceUpdateInput = z.infer<typeof invoiceUpdateSchema>;

export interface InvoiceDTO {
  id: string;
  kind: InvoiceKind;
  /** null while the invoice is a draft — a number is taken only at issue. */
  number: string | null;
  seriesYear: number | null;
  seriesSeq: number | null;
  status: InvoiceStatus;
  partnerId: string;
  /** Frozen at issue: the partner as they were on the document we sent. */
  partnerName: string;
  partnerLegalName: string | null;
  partnerAddress: string | null;
  partnerCity: string | null;
  partnerIce: string | null;
  issueDate: string;
  dueDate: string | null;
  periodMonth: string | null;
  subject: string | null;
  poNumber: string | null;
  poDate: string | null;
  taxRate: number;
  totalHt: number;
  totalTva: number;
  totalTtc: number;
  amountInWords: string | null;
  notes: string | null;
  correctsInvoiceId: string | null;
  correctsInvoiceNumber: string | null;
  issuedAt: string | null;
  paidAt: string | null;
  cancelledAt: string | null;
  createdAt: string | null;
  updatedAt: string | null;
  deletedAt: string | null;
  lines: InvoiceLineDTO[];
}

export const invoicesListQuerySchema = listQueryBaseSchema.extend({
  q: z.string().trim().optional(),
  status: invoiceStatusSchema.optional(),
  kind: invoiceKindSchema.optional(),
  partnerId: z.string().uuid().optional(),
  from: isoDateStringSchema.optional(),
  to: isoDateStringSchema.optional(),
});
export type InvoicesListQuery = z.infer<typeof invoicesListQuerySchema>;
export type InvoicesListResult = ListResult<InvoiceDTO>;

export const invoiceCreditNoteSchema = z.object({
  issueDate: isoDateStringSchema.optional(),
  reason: optionalText(500),
});
export type InvoiceCreditNoteInput = z.infer<typeof invoiceCreditNoteSchema>;

// ------------------------------------------------------- company settings

/**
 * Our own legal block, printed on every document. Every field is optional at
 * the schema level so the settings page can be filled in over several visits,
 * but `assertIssuable` (apps/api) refuses to issue an invoice while the
 * legally mandatory ones (name, address, ICE, IF, RC) are still blank —
 * better a hard stop at issue than a defective document in a partner's
 * accounting file.
 */
export const companySettingsSchema = z.object({
  name: optionalText(191),
  address: optionalText(500),
  city: optionalText(120),
  ice: optionalWhenBlank(iceSchema),
  ifNumber: optionalText(32),
  rc: optionalText(32),
  taxProfessionnelle: optionalText(32),
  phone: optionalText(32),
  email: optionalWhenBlank(z.string().trim().email().max(191)),
  bankName: optionalText(120),
  rib: optionalText(64),
  iban: optionalText(64),
  swift: optionalText(32),
  invoiceFooter: optionalText(1000),
});
export type CompanySettingsInput = z.infer<typeof companySettingsSchema>;

export interface CompanySettingsDTO {
  name: string | null;
  address: string | null;
  city: string | null;
  ice: string | null;
  ifNumber: string | null;
  rc: string | null;
  taxProfessionnelle: string | null;
  phone: string | null;
  email: string | null;
  bankName: string | null;
  rib: string | null;
  iban: string | null;
  swift: string | null;
  invoiceFooter: string | null;
  updatedAt: string | null;
}

/** Fields Moroccan law requires on the issuer's side of every invoice. */
export const REQUIRED_COMPANY_FIELDS = [
  "name",
  "address",
  "ice",
  "ifNumber",
  "rc",
] as const satisfies readonly (keyof CompanySettingsDTO)[];
